import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { PassForm, PassCard, EmptyState, BelongingsSection, getStatusBadge, canEditPass, canCancelPass, emptyPass } from "../components/PassComponents";
import ExitPassTicket from "../components/ExitPassTicket";
import {
  CheckCircle2, XCircle, Clock, User, Building2,
  Plane, MapPin, Calendar, MessageSquare, LogOut, FileText, QrCode, Plus, Package, ChevronDown, ChevronUp
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ApproverDashboard() {
  const { user, logout, getAuthHeaders } = useAuth();
  const [pendingApprovals, setPendingApprovals] = useState([]);
  const [myPasses, setMyPasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPass, setSelectedPass] = useState(null);
  const [showActionDialog, setShowActionDialog] = useState(false);
  const [actionType, setActionType] = useState("");
  const [comments, setComments] = useState("");
  const [processing, setProcessing] = useState(false);
  const [showQRDialog, setShowQRDialog] = useState(false);
  const [qrPass, setQrPass] = useState(null);

  // My Passes management state
  const [showNewPassDialog, setShowNewPassDialog] = useState(false);
  const [newPass, setNewPass] = useState(emptyPass);
  const [submitting, setSubmitting] = useState(false);
  const [editingPass, setEditingPass] = useState(null);
  const [cancelPass, setCancelPass] = useState(null);
  const [cancelNote, setCancelNote] = useState("");
  const [myBelongings, setMyBelongings] = useState([]);
  const [showHistory, setShowHistory] = useState(false);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchData = async () => {
    try {
      const [approvals, passes, belongings] = await Promise.all([
        axios.get(`${API}/pending-approvals`, { headers: getAuthHeaders() }),
        axios.get(`${API}/exit-passes`, { headers: getAuthHeaders() }),
        axios.get(`${API}/belongings/me`, { headers: getAuthHeaders() }),
      ]);
      setPendingApprovals(approvals.data);
      setMyPasses(passes.data);
      setMyBelongings(belongings.data);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleAction = async () => {
    if (!selectedPass || !actionType) return;
    setProcessing(true);
    try {
      await axios.post(
        `${API}/exit-passes/${selectedPass.id}/approve`,
        { action: actionType, comments },
        { headers: getAuthHeaders() }
      );
      toast.success(actionType === "approve" ? "Exit pass approved!" : "Exit pass rejected");
      setShowActionDialog(false);
      setSelectedPass(null);
      setComments("");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to process");
    } finally {
      setProcessing(false);
    }
  };

  const openActionDialog = (pass, type) => {
    setSelectedPass(pass);
    setActionType(type);
    setShowActionDialog(true);
  };

  // My Passes handlers
  const handleSubmitPass = async (e) => {
    e.preventDefault();
    if (!newPass.transport_type || !newPass.destination || !newPass.purpose ||
        !newPass.departure_date || !newPass.departure_time ||
        !newPass.arrival_date || !newPass.arrival_time) {
      toast.error("Please fill in all required fields");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/exit-passes`, newPass, { headers: getAuthHeaders() });
      toast.success("Exit pass request submitted!");
      setShowNewPassDialog(false);
      setNewPass(emptyPass);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit exit pass");
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditPass = async (e) => {
    e.preventDefault();
    if (!editingPass) return;
    setSubmitting(true);
    try {
      await axios.put(`${API}/exit-passes/${editingPass.id}`, {
        transport_type: editingPass.transport_type,
        transport_details: editingPass.transport_details,
        destination: editingPass.destination,
        purpose: editingPass.purpose,
        departure_date: editingPass.departure_date,
        departure_time: editingPass.departure_time,
        arrival_date: editingPass.arrival_date,
        arrival_time: editingPass.arrival_time,
      }, { headers: getAuthHeaders() });
      toast.success("Exit pass updated!");
      setEditingPass(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update");
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancelPass = async () => {
    if (!cancelPass) return;
    if (!cancelNote.trim()) {
      toast.error("Please provide a reason for cancellation");
      return;
    }
    setSubmitting(true);
    try {
      await axios.delete(`${API}/exit-passes/${cancelPass.id}`, {
        headers: getAuthHeaders(),
        data: { cancel_note: cancelNote.trim() }
      });
      toast.success("Exit pass cancelled");
      setCancelPass(null);
      setCancelNote("");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to cancel");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50">
        <div className="animate-pulse text-primary font-heading text-xl">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50">
      {/* Header */}
      <header className="bg-white border-b border-stone-200 sticky top-0 z-30">
        <div className="max-w-5xl mx-auto px-4 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-amber-500 to-amber-600 rounded-xl flex items-center justify-center">
              <Clock className="w-5 h-5 text-white" strokeWidth={1.5} />
            </div>
            <div>
              <h1 className="font-heading font-bold text-stone-800">{user?.name}</h1>
              <p className="text-xs text-stone-500">{user?.employee_id} • {user?.role?.toUpperCase()}</p>
            </div>
          </div>
          <Button variant="ghost" onClick={logout} data-testid="logout-btn" className="text-stone-500 hover:text-red-600 gap-2">
            <LogOut className="w-4 h-4" strokeWidth={1.5} />
            <span className="hidden sm:inline">Logout</span>
          </Button>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-6">
        <Tabs defaultValue="approvals" className="space-y-4">
          <TabsList className="bg-white border border-stone-200 p-1 rounded-xl w-full grid grid-cols-2">
            <TabsTrigger value="approvals" data-testid="approvals-tab" className="rounded-lg data-[state=active]:bg-primary data-[state=active]:text-white gap-2 relative">
              <Clock className="w-4 h-4" />
              Pending Approvals
              {pendingApprovals.length > 0 && (
                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] font-bold w-5 h-5 rounded-full flex items-center justify-center">
                  {pendingApprovals.length}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="mypasses" data-testid="mypasses-tab" className="rounded-lg data-[state=active]:bg-primary data-[state=active]:text-white gap-2">
              <FileText className="w-4 h-4" />
              My Exit Passes
            </TabsTrigger>
          </TabsList>

          {/* Pending Approvals Tab */}
          <TabsContent value="approvals">
            {pendingApprovals.length === 0 ? (
              <Card className="border-dashed border-2 border-stone-200">
                <CardContent className="py-16 text-center">
                  <div className="w-20 h-20 bg-emerald-50 rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <CheckCircle2 className="w-10 h-10 text-emerald-500" strokeWidth={1.5} />
                  </div>
                  <h3 className="font-heading font-semibold text-stone-700 mb-2 text-lg">All caught up!</h3>
                  <p className="text-stone-500">No pending approvals at the moment</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-4">
                {pendingApprovals.map((pass) => (
                  <Card key={pass.id} className="bg-white border-stone-200 shadow-card hover:shadow-float transition-all">
                    <CardContent className="p-5">
                      <div className="flex flex-col lg:flex-row lg:items-start gap-5">
                        <div className="flex items-start gap-3 lg:w-1/4">
                          <div className="w-11 h-11 bg-primary/10 rounded-xl flex items-center justify-center flex-shrink-0">
                            <User className="w-5 h-5 text-primary" strokeWidth={1.5} />
                          </div>
                          <div>
                            <h3 className="font-heading font-semibold text-stone-800">{pass.staff_name}</h3>
                            <p className="text-sm text-stone-500 font-mono">{pass.employee_id}</p>
                            <div className="flex items-center gap-1 mt-1">
                              <Building2 className="w-3 h-3 text-stone-400" />
                              <span className="text-xs text-stone-400">{pass.department_name || "\u2014"}</span>
                            </div>
                          </div>
                        </div>
                        <div className="flex-1 grid sm:grid-cols-2 gap-3">
                          <div className="flex items-start gap-2">
                            <MapPin className="w-4 h-4 text-primary mt-0.5" strokeWidth={1.5} />
                            <div>
                              <p className="text-xs text-stone-400 uppercase tracking-wider">Destination</p>
                              <p className="font-medium text-stone-700">{pass.destination}</p>
                            </div>
                          </div>
                          <div className="flex items-start gap-2">
                            <Plane className="w-4 h-4 text-stone-400 mt-0.5" strokeWidth={1.5} />
                            <div>
                              <p className="text-xs text-stone-400 uppercase tracking-wider">Transport</p>
                              <p className="text-stone-600 capitalize">{pass.transport_type?.replace(/_/g, " ")}</p>
                            </div>
                          </div>
                          <div className="flex items-start gap-2">
                            <Calendar className="w-4 h-4 text-stone-400 mt-0.5" strokeWidth={1.5} />
                            <div>
                              <p className="text-xs text-stone-400 uppercase tracking-wider">Departure</p>
                              <p className="text-stone-600">{pass.departure_date} • {pass.departure_time}</p>
                            </div>
                          </div>
                          <div className="flex items-start gap-2">
                            <Calendar className="w-4 h-4 text-stone-400 mt-0.5" strokeWidth={1.5} />
                            <div>
                              <p className="text-xs text-stone-400 uppercase tracking-wider">Return</p>
                              <p className="text-stone-600">{pass.arrival_date} • {pass.arrival_time}</p>
                            </div>
                          </div>
                        </div>
                        <div className="flex lg:flex-col gap-2 lg:w-28">
                          <Button onClick={() => openActionDialog(pass, "approve")} data-testid={`approve-btn-${pass.id}`} className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white gap-1.5 rounded-xl text-sm">
                            <CheckCircle2 className="w-4 h-4" /> Approve
                          </Button>
                          <Button onClick={() => openActionDialog(pass, "reject")} data-testid={`reject-btn-${pass.id}`} variant="outline" className="flex-1 border-red-200 text-red-600 hover:bg-red-50 gap-1.5 rounded-xl text-sm">
                            <XCircle className="w-4 h-4" /> Reject
                          </Button>
                        </div>
                      </div>
                      <div className="mt-4 pt-3 border-t border-stone-100 flex items-start gap-2">
                        <MessageSquare className="w-4 h-4 text-stone-400 mt-0.5" strokeWidth={1.5} />
                        <div>
                          <p className="text-xs text-stone-400 uppercase tracking-wider mb-0.5">Purpose</p>
                          <p className="text-sm text-stone-600">{pass.purpose}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* My Passes Tab */}
          <TabsContent value="mypasses" className="space-y-4">
            <Dialog open={showNewPassDialog} onOpenChange={setShowNewPassDialog}>
              <DialogTrigger asChild>
                <Button className="w-full bg-primary hover:bg-primary/90 text-white rounded-xl h-14 text-base shadow-lg shadow-primary/20 gap-2" data-testid="new-pass-btn">
                  <Plus className="w-5 h-5" strokeWidth={2} />
                  New Exit Pass Request
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="font-heading">New Exit Pass Request</DialogTitle>
                  <DialogDescription>Fill in the details for your exit pass</DialogDescription>
                </DialogHeader>
                <PassForm pass={newPass} setPass={setNewPass} onSubmit={handleSubmitPass} submitting={submitting} btnLabel="Submit Request" />
              </DialogContent>
            </Dialog>

            {(() => {
              const activePasses = myPasses.filter(p => ["pending", "approved"].includes(p.status));
              const historyPasses = myPasses.filter(p => ["used", "cancelled", "rejected"].includes(p.status));
              return (
                <>
                  {activePasses.length === 0 ? (
                    <EmptyState icon={FileText} message="No active exit passes. Create a new request above." />
                  ) : (
                    <div className="space-y-3">
                      {activePasses.map((pass) => (
                        <PassCard key={pass.id} pass={pass} getStatusBadge={getStatusBadge}
                          onViewQR={() => { setQrPass(pass); setShowQRDialog(true); }}
                          onEdit={() => setEditingPass({ ...pass, purpose_type: pass.purpose })}
                          onCancel={() => { setCancelPass(pass); setCancelNote(""); }}
                          canEdit={canEditPass(pass)} canCancel={canCancelPass(pass)} />
                      ))}
                    </div>
                  )}

                  <BelongingsSection belongings={myBelongings} testIdPrefix="my" />

                  {historyPasses.length > 0 && (
                    <div>
                      <button onClick={() => setShowHistory(!showHistory)} className="flex items-center justify-between w-full p-3 bg-stone-100 rounded-xl hover:bg-stone-200 transition-colors" data-testid="toggle-history-btn">
                        <span className="font-heading font-semibold text-stone-700 text-sm">View Pass History ({historyPasses.length})</span>
                        {showHistory ? <ChevronUp className="w-4 h-4 text-stone-500" /> : <ChevronDown className="w-4 h-4 text-stone-500" />}
                      </button>
                      {showHistory && (
                        <div className="space-y-3 mt-3">
                          {historyPasses.map((pass) => (
                            <PassCard key={pass.id} pass={pass} getStatusBadge={getStatusBadge}
                              onViewQR={() => {}} onEdit={() => {}} onCancel={() => {}} canEdit={false} canCancel={false} />
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </>
              );
            })()}
          </TabsContent>
        </Tabs>
      </main>

      {/* Approve/Reject Dialog */}
      <Dialog open={showActionDialog} onOpenChange={setShowActionDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              {actionType === "approve" ? (
                <><CheckCircle2 className="w-5 h-5 text-emerald-600" /> Approve Exit Pass</>
              ) : (
                <><XCircle className="w-5 h-5 text-red-600" /> Reject Exit Pass</>
              )}
            </DialogTitle>
            <DialogDescription>
              {actionType === "approve" ? "Confirm approval for this request." : "Please provide a reason for rejection."}
            </DialogDescription>
          </DialogHeader>
          {selectedPass && (
            <div className="space-y-4 py-4">
              <div className="bg-stone-50 rounded-xl p-4">
                <p className="font-medium text-stone-800">{selectedPass.staff_name}</p>
                <p className="text-sm text-stone-500">{selectedPass.destination} • {selectedPass.departure_date}</p>
              </div>
              <div className="space-y-2">
                <label className="text-sm font-semibold text-stone-600">
                  Comments {actionType === "reject" && <span className="text-red-500">*</span>}
                </label>
                <Textarea data-testid="approval-comments" placeholder={actionType === "approve" ? "Optional comments..." : "Reason for rejection..."} value={comments} onChange={(e) => setComments(e.target.value)} rows={3} />
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <Button variant="outline" onClick={() => setShowActionDialog(false)} className="rounded-xl">Cancel</Button>
                <Button onClick={handleAction} disabled={processing || (actionType === "reject" && !comments)} data-testid="confirm-action-btn" className={`rounded-xl ${actionType === "approve" ? "bg-emerald-600 hover:bg-emerald-700" : "bg-red-600 hover:bg-red-700"} text-white`}>
                  {processing ? "Processing..." : actionType === "approve" ? "Confirm Approval" : "Confirm Rejection"}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Pass Dialog */}
      <Dialog open={!!editingPass} onOpenChange={(v) => !v && setEditingPass(null)}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">Edit Exit Pass</DialogTitle>
            <DialogDescription>Update your request details</DialogDescription>
          </DialogHeader>
          {editingPass && (
            <PassForm pass={editingPass} setPass={setEditingPass} onSubmit={handleEditPass} submitting={submitting} btnLabel="Save Changes" />
          )}
        </DialogContent>
      </Dialog>

      {/* Cancel Pass Dialog */}
      <Dialog open={!!cancelPass} onOpenChange={(v) => !v && setCancelPass(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-heading text-red-600">Cancel Exit Pass</DialogTitle>
            <DialogDescription>{cancelPass?.destination} — {cancelPass?.departure_date}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-sm font-semibold">Reason for cancellation *</Label>
              <Textarea value={cancelNote} onChange={(e) => setCancelNote(e.target.value)} placeholder="Please provide a reason..." className="mt-2" rows={3} data-testid="cancel-note-input" />
            </div>
            <div className="flex gap-3">
              <Button variant="outline" className="flex-1" onClick={() => setCancelPass(null)}>Keep Pass</Button>
              <Button onClick={handleCancelPass} disabled={submitting || !cancelNote.trim()} data-testid="confirm-cancel-btn" className="flex-1 bg-red-500 hover:bg-red-600 text-white">
                {submitting ? "Cancelling..." : "Cancel Pass"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* QR Dialog */}
      <Dialog open={showQRDialog} onOpenChange={setShowQRDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">Exit Pass QR Code</DialogTitle>
          </DialogHeader>
          {qrPass && <ExitPassTicket pass={qrPass} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}
